import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { IdeaTask, TaskStatus, Priority } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { 
  MoreHorizontal, 
  Trash2, 
  AlertCircle,
  CheckCircle2,
  Circle,
  Clock,
  FlaskConical,
  Hammer,
  Search,
  Users,
  FileText,
  GripVertical,
  Sparkles,
  Pencil,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface TaskCardProps {
  task: IdeaTask;
  onDelete: (taskId: string) => Promise<boolean>;
  onEdit?: (task: IdeaTask) => void;
  isDragging?: boolean;
  dragHandleProps?: any;
}

const STATUS_CONFIG: Record<TaskStatus, { label: string; icon: typeof Circle; className: string }> = {
  next: { label: 'Next', icon: Circle, className: 'text-muted-foreground' },
  doing: { label: 'Doing', icon: Clock, className: 'text-primary' },
  blocked: { label: 'Blocked', icon: AlertCircle, className: 'text-destructive' },
  done: { label: 'Done', icon: CheckCircle2, className: 'text-primary' },
};

const TYPE_ICONS: Record<string, typeof FlaskConical> = {
  experiment: FlaskConical,
  build: Hammer,
  research: Search,
  outreach: Users,
  admin: FileText,
};

const PRIORITY_STYLES: Record<Priority, string> = {
  low: 'bg-muted/50 text-muted-foreground',
  medium: 'bg-secondary/30 text-secondary-foreground',
  high: 'bg-primary/20 text-primary',
};

export function TaskCard({ task, onDelete, onEdit, isDragging, dragHandleProps }: TaskCardProps) {
  const [loading, setLoading] = useState(false);
  
  const StatusIcon = STATUS_CONFIG[task.status].icon;
  const TypeIcon = TYPE_ICONS[task.task_type] || FileText;

  const handleDelete = async () => {
    if (confirm('Delete this task?')) {
      setLoading(true);
      await onDelete(task.id);
      setLoading(false);
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger edit when clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('[role="menuitem"]')) {
      return;
    }
    onEdit?.(task);
  };

  return (
    <div 
      className={cn(
        "group bg-card border rounded-lg p-4 space-y-2.5 calm-transition",
        onEdit && "cursor-pointer hover:border-primary/30",
        task.status === 'done' && "opacity-60",
        task.status === 'blocked' && "border-destructive/30 bg-destructive/5",
        loading && "opacity-50 pointer-events-none",
        isDragging && "ring-2 ring-primary shadow-xl bg-card rotate-1 scale-[1.02]"
      )}
      onClick={handleCardClick}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {dragHandleProps && (
            <div 
              {...dragHandleProps} 
              className="cursor-grab active:cursor-grabbing touch-none"
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground/50 hover:text-muted-foreground" />
            </div>
          )}
          <StatusIcon className={cn("h-4 w-4 shrink-0", STATUS_CONFIG[task.status].className)} />
          <span className={cn(
            "text-sm font-medium truncate",
            task.status === 'done' && "line-through text-muted-foreground"
          )}>
            {task.title}
          </span>
          {task.source === 'ai' && (
            <Sparkles className="h-3 w-3 text-primary/50 shrink-0" />
          )}
        </div>
        
        <div className="flex items-center gap-1">
          {onEdit && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(task);
              }}
            >
              <Pencil className="h-3 w-3" />
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onEdit && (
                <>
                  <DropdownMenuItem onClick={() => onEdit(task)}>
                    <Pencil className="mr-2 h-3.5 w-3.5" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Description */}
      {task.description && (
        <p className="text-xs text-muted-foreground line-clamp-2 pl-6">
          {task.description}
        </p>
      )}

      {/* Blocked reason */}
      {task.status === 'blocked' && task.blocked_reason && (
        <p className="text-xs text-destructive/80 pl-6 italic">
          Blocked: {task.blocked_reason}
        </p>
      )}

      {/* Done note */}
      {task.status === 'done' && task.done_note && (
        <p className="text-xs text-primary/70 pl-6 italic">
          ✓ {task.done_note}
        </p>
      )}

      {/* Meta */}
      <div className="flex items-center gap-2 pl-6 flex-wrap">
        <Badge variant="outline" className="h-5 text-[10px] gap-1">
          <TypeIcon className="h-3 w-3" />
          {task.task_type}
        </Badge>
        <Badge className={cn("h-5 text-[10px]", PRIORITY_STYLES[task.priority])}>
          {task.priority}
        </Badge>
        {task.estimated_hours && (
          <span className="text-[10px] text-muted-foreground">
            {task.estimated_hours}h
          </span>
        )}
        {task.due_date && (
          <span className="text-[10px] text-muted-foreground">
            Due {format(new Date(task.due_date), 'MMM d')}
          </span>
        )}
      </div>
    </div>
  );
}

// Sortable wrapper for drag-and-drop
interface SortableTaskCardProps {
  task: IdeaTask;
  onDelete: (taskId: string) => Promise<boolean>;
  onEdit?: (task: IdeaTask) => void;
}

export function SortableTaskCard({ task, onDelete, onEdit }: SortableTaskCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <TaskCard
        task={task}
        onDelete={onDelete}
        onEdit={onEdit}
        isDragging={isDragging}
        dragHandleProps={listeners}
      />
    </div>
  );
}